import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { HttpErrorResponse } from "@angular/common/http";
import { MatDialogRef } from "@angular/material/dialog";
import { ApiHttpService } from "../../services/api-http.service";
import { ContractLinkCodeError, ContractLinkStep } from "../../models";

/**
 * Must match `OTPCooldown` in the backend's internal/googlelink/contractotp.go.
 * The backend answers "sent" even when it stays quiet inside its cooldown, so
 * a resend this screen allows earlier than that is a mail that never comes.
 */
const RESEND_COOLDOWN_SECONDS = 60;
/** The backend sends six digits; anything else cannot be the code. */
const CODE_PATTERN = /^\d{6}$/;

/**
 * The way in for a customer whose Google address we do not know.
 *
 * They name the address their policy is on, prove it with a code, and the
 * backend builds the account on that address with the Google account attached.
 * The modal is opened by the login screen when a Google sign-in comes back with
 * `error.social_link_required`.
 *
 * Two rules shape the whole flow, and neither can be enforced by the backend:
 *
 *  - The address step must not say whether an address holds a policy. The
 *    request endpoint answers the same either way, and the modal advances to
 *    the code step whatever happens - a network error included. Any difference
 *    here would be a public oracle for who holds a Calingo policy.
 *  - A refused code must not say why. `invalid_code` covers a wrong code, an
 *    expired code and an address with no contract, and the copy merges them the
 *    same way the backend does.
 */
@Component({
  selector: 'cpf-contract-link-modal',
  templateUrl: './contract-link-modal.component.html',
  styleUrls: ['./contract-link-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContractLinkModalComponent implements OnDestroy {
  step: ContractLinkStep = 'email';
  email = '';
  code = '';
  busy = false;
  codeError: ContractLinkCodeError | null = null;
  resendCooldown = 0;

  private cooldownInterval?: ReturnType<typeof setInterval>;

  constructor(public dialogRef: MatDialogRef<ContractLinkModalComponent>,
              private apiHttpService: ApiHttpService) {
  }

  get canSubmitEmail(): boolean {
    // Only emptiness is checked. A "that does not look like an address" rule
    // would refuse some addresses before the backend ever sees them, which is
    // one more way for the screen to answer a question it must not answer.
    return !this.busy && this.email.trim().length > 0;
  }

  get canSubmitCode(): boolean {
    return !this.busy && CODE_PATTERN.test(this.code.trim());
  }

  get canResend(): boolean {
    return !this.busy && this.resendCooldown === 0;
  }

  /** Asks for the code, then advances - whatever the endpoint answered. */
  async submitEmail(): Promise<void> {
    if (!this.canSubmitEmail) {
      return;
    }

    this.email = this.email.trim();
    this.code = '';
    this.codeError = null;

    await this.sendCode();

    this.step = 'code';
  }

  async resendCode(): Promise<void> {
    if (!this.canResend) {
      return;
    }

    this.codeError = null;

    await this.sendCode();
  }

  /** Goes back to the address step, so a typo can be corrected. */
  changeEmail(): void {
    if (this.busy) {
      return;
    }

    this.step = 'email';
    this.code = '';
    this.codeError = null;
    this.clearCooldown();
  }

  /**
   * Hands the code over. On success the browser leaves for Google; the busy
   * state is deliberately left standing, because the page is on its way out.
   */
  async submitCode(): Promise<void> {
    if (!this.canSubmitCode) {
      return;
    }

    this.busy = true;
    this.codeError = null;

    try {
      const { authUrl } = await this.apiHttpService.verifyContractLinkCode(this.email, this.code.trim());

      if (!authUrl) {
        throw new Error('No authUrl in the /link/contract/verify response');
      }

      this.step = 'redirect';
      this.clearCooldown();
      this.redirectTo(authUrl);
    } catch (error) {
      this.busy = false;
      this.codeError = this.toCodeError(error);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.clearCooldown();
  }

  /** Isolated so a test can watch the redirect instead of performing it. */
  private redirectTo(url: string): void {
    window.location.href = url;
  }

  private async sendCode(): Promise<void> {
    this.busy = true;

    try {
      await this.apiHttpService.requestContractLinkCode(this.email);
    } catch (error) {
      // Swallowed on purpose. The customer is told the code is on its way in
      // every case, so the screen says the same for an address we know, an
      // address we do not, and a request that never arrived.
      console.error('Could not ask for the contract confirmation code', error);
    } finally {
      this.busy = false;
    }

    this.startCooldown();
  }

  private toCodeError(error: unknown): ContractLinkCodeError {
    if (!(error instanceof HttpErrorResponse)) {
      console.error('Could not verify the contract confirmation code', error);
      return 'generic';
    }

    if (error.status === 400) {
      return 'invalid-code';
    }

    if (error.status === 429) {
      return 'too-many-attempts';
    }

    console.error('Could not verify the contract confirmation code', error);
    return 'generic';
  }

  private startCooldown(): void {
    this.clearCooldown();
    this.resendCooldown = RESEND_COOLDOWN_SECONDS;
    this.cooldownInterval = setInterval(() => {
      this.resendCooldown -= 1;
      if (this.resendCooldown <= 0) {
        this.clearCooldown();
      }
    }, 1000);
  }

  private clearCooldown(): void {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
      this.cooldownInterval = undefined;
    }
    this.resendCooldown = 0;
  }
}
